import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Product } from '../../../stores/appReportsStore'
import { calculateTotalSpent } from '../../../services/import/importProductIdsService'
import { currencyFormatter } from '../../../services/helperService'
import { editOptionProductCardClass } from '../../../styling/stylingHelper'
import { SupplierLogo } from '../../ui/SupplierLogo'

interface EditOptionProductCardProps {
  parentProduct: Product
  product: Product
}

export default function EditOptionProductCard({
  parentProduct,
  product,
}: EditOptionProductCardProps) {
  const { t } = useTranslation()
  const [imgSrc, setImgSrc] = useState(product.src)

  const totalPrice = calculateTotalSpent(product)
  const parentPrice = calculateTotalSpent(parentProduct)
  const savingsAmt = totalPrice - parentPrice
  const savingsText = currencyFormatter.format(savingsAmt * -1)
  const savingsPct = parentPrice > 0 ? (savingsAmt / parentPrice) * 100 : 0

  const pctClass =
    savingsPct === 0
      ? 'text-muted dark:text-muted-dark'
      : savingsPct < 0
      ? 'text-textGreen dark:text-textGreen-dark'
      : 'text-textRed dark:text-textRed-dark'

  const unitInfo = `${currencyFormatter.format(product.unitPrice)}/${product.unitPriceUnit}`
  const stdAmount = product.useCombo
    ? product.combo?.standardizedAmount ?? product.standardizedAmount
    : product.standardizedAmount
  const isGrammOrMl = ['g', 'ml'].includes(product.standardUnit)
  const totalQuantity = isGrammOrMl
    ? (product.quantity * stdAmount) / 1000
    : product.quantity * stdAmount
  const qty = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(totalQuantity)
  const stdUnit =
    product.standardUnit === 'g'
      ? 'kg'
      : product.standardUnit === 'ml'
      ? 'ltr'
      : product.standardUnit

  const comparable =
    (['stuk', 'piece'].includes(parentProduct.standardUnit) &&
      ['stuk', 'piece'].includes(product.standardUnit)) ||
    (['g', 'ml'].includes(parentProduct.standardUnit) &&
      ['g', 'ml'].includes(product.standardUnit))

  return (
    <div
      className={`${editOptionProductCardClass} flex flex-col md:flex-row p-3 w-full`}
    >
      {/* Top: image + main info */}
      <div className="flex w-full">
        {/* Image */}
        <div className="border border-borderLightGrey dark:border-borderLightGrey-dark rounded-xl">
          <img
            src={imgSrc}
            alt={product.name}
            className="w-[70px] h-[70px] object-cover rounded-xl border-2 border-white dark:border-card-dark"
            onError={e => {
              e.currentTarget.onerror = null
              setImgSrc('/no_photo.png')
            }}
          />
        </div>

        {/* Info */}
        <div className="flex-1 flex flex-col justify-between ml-3 min-w-0">
          {/* Qty/Name (+ mobile articleId) + desktop price/savings */}
          <div className="flex items-stretch justify-between">
            {/* Qty + Name */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex items-start">
                <div className="w-fit text-xs font-medium rounded-lg bg-textBlue/10 dark:bg-textBlue-dark/10 px-2 py-0.5 text-textBlue dark:text-textBlue-dark mb-1">
                  {qty}
                  {stdUnit}
                </div>
                <div className="w-fit text-xs font-medium rounded-lg bg-borderGrey dark:bg-borderGrey-dark px-2 py-0.5 text-tertiary dark:text-tertiary-dark mb-1 block md:hidden ml-2">
                  ID: {product.articleId}
                </div>
              </div>
              <a
                href={product.link}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline text-text dark:text-text-dark"
              >
                <h4 className="truncate font-semibold" title={product.name}>
                  {product.name}
                </h4>
              </a>
            </div>

            {/* Desktop-only unit price */}
            <div className="hidden md:flex self-center w-px h-[60%] bg-borderGrey dark:bg-borderGrey-dark my-1 mx-1.5" />
            <div className="hidden md:flex flex-col px-2">
              <span className="text-base text-muted dark:text-muted-dark">
                {t('product.price')}
              </span>
              <span className="text-primary dark:text-primary-dark font-semibold truncate">
                {unitInfo}
              </span>
            </div>

            {/* Desktop-only savings */}
            <div className="hidden md:flex self-center w-px h-[60%] bg-borderGrey dark:bg-borderGrey-dark my-1 mx-1.5" />
            <div className="hidden md:flex flex-col items-end px-2">
              <span className="font-semibold truncate">{savingsText}</span>
              <span
                className={`text-xs ${comparable ? pctClass : 'text-muted dark:text-muted-dark'}`}
              >
                {comparable ? savingsPct.toFixed(0) : '-'}%
              </span>
            </div>
          </div>

          {/* bottom row (brand · articleId · logo) */}
          <div className="flex items-center mt-2">
            <span className="text-muted dark:text-muted-dark mr-1">
              {t('product.brand')}
            </span>
            <span className="text-secondary dark:text-secondary-dark font-medium">
              {product.brand}
            </span>
            <span className="text-muted dark:text-muted-dark mx-1 font-semibold hidden md:block">
              ·
            </span>
            <span className="text-sm text-muted dark:text-muted-dark font-medium hidden md:block">
              {product.articleId}
            </span>
            <span className="text-muted dark:text-muted-dark mx-1 font-semibold">
              ·
            </span>
            <SupplierLogo
              platform={product.platform}
              alt={product.platform}
              size="small"
              className="inline-block align-middle"
            />
          </div>
        </div>
      </div>

      {/* Mobile-only price / savings row below */}
      <div className="flex justify-between mt-2 md:hidden px-2">
        <div className="flex flex-col">
          <span className="text-sm text-muted dark:text-muted-dark">
            {t('product.price')}
          </span>
          <span className="font-semibold">{unitInfo}</span>
        </div>
        <div className="flex flex-col items-end">
          <span className="font-semibold">{savingsText}</span>
          <span
            className={`text-xs ${comparable ? pctClass : 'text-muted dark:text-muted-dark'}`}
          >
            {comparable ? savingsPct.toFixed(0) : '-'}%
          </span>
        </div>
      </div>
    </div>
  )
}
